import { RecurringRequestList } from '@back-office/http';
import {
  GcpBillerEnrollmentListTable,
  GcpBox,
  GcpDateRangeRecurring,
  GcpSearchBox,
  GcpSelect,
  GcpSelectStatus,
} from '@back-office/ui';
import { Button, Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { DateRange } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import React, { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ExportToXLSXDialog from '../biller-enrollment-list/ExportToXLSXDialog';
import RecurringPaymentDetail from '../BillerEnrollmentDetail';
import useViewModel from './viewmodel';

export interface Store {
  handleOnChangeDatePicker: (date: DateRange<Date>) => void;
  handleOnChangePage: (newPage: number) => void;
  handleOnClear: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnClickDatePicker: () => void;
  handleOnClickRow: (recurringRequest: RecurringRequestList) => void;
  handleOnCloseDatePicker: () => void;
  handleOnGo: () => void;
  handleKeySearch: (searchValue: string) => void;
  handleWalletIdSearch: (searchValue: string) => void;
  handleFullNameSearch: (searchValue: string) => void;
  handleMobileNumberSearch: (searchValue: string) => void;
  handleStatusSearch: (value: string) => void;
  handleEnrollmentSearch: (value: string) => void;
  handleOnClickBreadcrumbs: () => void;
  date: DateRange<Date>;
  openCalendar: boolean;
  page: number;
  count: number;
  query: string;
  start: number;
  enrollment_id_or_reference_no: string;
  enrollment_type_id: string;
  wallet_id: string;
  full_name: string;
  mobile_no: string;
  status: string;
  isDetail: boolean;
  recurringId: string | null;
}

const BillerEnrollment: FC = observer(() => {
  const { t } = useTranslation();
  const {
    state,
    on,
    toggle,
    recurringRequest,
    enrollmentTypeDropData,
    isEnrollmentType,
  } = useViewModel();

  return state.isDetail ? (
    <RecurringPaymentDetail
      handleOnClickBreadcrumbs={state.handleOnClickBreadcrumbs}
      query={state.query}
      recurringId={state.recurringId}
    />
  ) : (
    <GcpBox>
      <Grid container alignItems="center" justify="center" spacing={4} xs={12}>
        <Grid item xs={3}>
          <Typography variant="h3">
            {t('biller_enrollment_list.list')}
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <GcpBox display="flex" justifyContent="flex-end">
            <Button
              color="primary"
              size="large"
              startIcon={
                <svg
                  fill="none"
                  height="17"
                  viewBox="0 0 16 17"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.88736 11.1574C7.90072 11.1744 7.9178 11.1883 7.93729 11.1977C7.95678 11.2072 7.97818 11.2122 7.99986 11.2122C8.02154 11.2122 8.04294 11.2072 8.06243 11.1977C8.08192 11.1883 8.099 11.1744 8.11236 11.1574L10.1124 8.62701C10.1856 8.53415 10.1195 8.39665 9.99986 8.39665H8.67665V2.35379C8.67665 2.27522 8.61236 2.21094 8.53379 2.21094H7.46236C7.38379 2.21094 7.3195 2.27522 7.3195 2.35379V8.39487H5.99986C5.88022 8.39487 5.81415 8.53237 5.88736 8.62522L7.88736 11.1574ZM14.5356 10.5324H13.4641C13.3856 10.5324 13.3213 10.5967 13.3213 10.6752V13.4252H2.67843V10.6752C2.67843 10.5967 2.61415 10.5324 2.53557 10.5324H1.46415C1.38557 10.5324 1.32129 10.5967 1.32129 10.6752V14.2109C1.32129 14.527 1.57665 14.7824 1.89272 14.7824H14.107C14.4231 14.7824 14.6784 14.527 14.6784 14.2109V10.6752C14.6784 10.5967 14.6141 10.5324 14.5356 10.5324Z"
                    fill="white"
                  />
                </svg>
              }
              style={{ boxShadow: 'none' }}
              type="button"
              variant="contained"
              onClick={() => toggle(true)}
            >
              {t('biller_enrollment_list.export.button')}
            </Button>
          </GcpBox>
        </Grid>
      </Grid>

      <Grid
        container
        item
        alignItems="center"
        justify="center"
        spacing={4}
        xs={12}
      >
        <Grid item xs={6}>
          <GcpSearchBox
            handleSearch={state.handleKeySearch}
            label={t('biller_enrollment_list.search.key')}
            value={state.enrollment_id_or_reference_no}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpSearchBox
            handleSearch={state.handleWalletIdSearch}
            label={t('biller_enrollment_list.search.wallet_id')}
            value={state.wallet_id}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpDateRangeRecurring
            date={state.date}
            handleOnChange={state.handleOnChangeDatePicker}
            handleOnClear={state.handleOnClear}
            handleOnClick={state.handleOnClickDatePicker}
            handleOnClose={state.handleOnCloseDatePicker}
            openCalendar={state.openCalendar}
          />
        </Grid>
      </Grid>
      <Grid
        container
        item
        alignItems="center"
        justify="center"
        spacing={4}
        xs={12}
      >
        <Grid item xs={isEnrollmentType ? 3 : 6}>
          <GcpSearchBox
            handleSearch={state.handleFullNameSearch}
            label={t('biller_enrollment_list.search.full_name')}
            value={state.full_name}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpSearchBox
            handleSearch={state.handleMobileNumberSearch}
            label={t('biller_enrollment_list.search.mobile_number')}
            value={state.mobile_no}
          />
        </Grid>
        <Grid item xs={3}>
          <GcpSelectStatus
            value={state.status.toLocaleLowerCase()}
            onChange={state.handleStatusSearch}
          />
        </Grid>

        {isEnrollmentType && (
          <Grid item xs={3}>
            <GcpSelect
              data={enrollmentTypeDropData}
              value={state.enrollment_type_id.toLocaleLowerCase()}
              onChange={state.handleEnrollmentSearch}
            />
          </Grid>
        )}
      </Grid>
      <GcpBox mt={3}>
        <GcpBillerEnrollmentListTable
          count={state.count}
          data={recurringRequest.list as RecurringRequestList[]}
          enrollmentTypeDropData={enrollmentTypeDropData}
          handleOnChangePage={state.handleOnChangePage}
          handleOnClickRow={state.handleOnClickRow}
          isEnrollmentType={isEnrollmentType}
          page={state.page}
          pagination={recurringRequest.pagination}
        />
      </GcpBox>
      <ExportToXLSXDialog handleOnClose={() => toggle(false)} open={on} />
    </GcpBox>
  );
});

export default BillerEnrollment;
